import { App, Plugin } from 'vue';
import { Mutex } from 'async-mutex';
import { JwtToken } from '@/types/authentication/JwtToken';
import { isProductionEnvironment, isStagingEnvironment, isAzureDomain } from '@/helpers/environment/environmentReader';

export class HomemoverApiClient {
    private mutex = new Mutex();
    private _jwtToken: JwtToken = { token: '', expiresIn: 0, utcExpiryDate: '' };
    private _baseUrl: string
    private readonly _apiRetryMax = 1;

    constructor() {
        this._baseUrl = this.getBaseApiUrl();
    }

    private getBaseApiUrl() {
        if (isProductionEnvironment()) {
            return "https://homemoverapi.azurewebsites.net/v3/";
        }

        if (isStagingEnvironment()) {
            return 'https://homemoverapi-staging.azurewebsites.net/v3/';
        }

        return 'https://homemoverapi-sandbox.azurewebsites.net/v3/';
    }

    public async httpGet<T>(url: string): Promise<T> {
        return await this.getApiResponse<T>(url, "GET")
    }

    public async httpPut<T>(url: string, data: any): Promise<T> {
        return await this.getApiResponse<T>(url, "PUT", data)
    }

    public async httpPost<T>(url: string, data: any): Promise<T> {
        return await this.getApiResponse<T>(url, "POST", data)
    }

    public async httpDelete<T>(url: string, data: any = {}): Promise<T> {
        return await this.getApiResponse<T>(url, "DELETE", data)
    }

    public async httpPutVoid(url: string, data: any): Promise<void> {
        await this.sendHttpRequest(url, "PUT", data)
    }

    public async httpPostVoid(url: string, data: any): Promise<void> {
        await this.sendHttpRequest(url, "POST", data)
    }

    public async httpDeleteVoid(url: string, data: any = {}): Promise<void> {
        await this.sendHttpRequest(url, "DELETE", data)
    }

    private async getJwtToken(): Promise<void> {
        const apiBaseUrl = this.getJwtBaseApiUrl();

        const result = await fetch(`${apiBaseUrl}/api/authorize`, {
            headers: {
                'Content-Type': 'application/json',
            },
            method: "POST"
        });

        this._jwtToken = await result.json();
    }
    
    private getJwtBaseApiUrl() {
        if (isProductionEnvironment()) {
            if(isAzureDomain() == true) 
            {
                return "https://homemover.azurewebsites.net"
            }

            return "https://move.propertybutton.ie";
        }

        if (isStagingEnvironment()) {
            if(isAzureDomain() == true) 
            {
                return "https://homemover-staging.azurewebsites.net"
            }

            return 'https://move-staging.propertybutton.ie';
        }

            if(isAzureDomain() == true) 
            {
                return "https://homemover-sandbox.azurewebsites.net"
            }

        return 'https://move-sandbox.propertybutton.ie';        
    }

    private isJwtTokenRefreshRequired(): boolean {
        const fiveMinutuesInMilliseconds = 300000;
        const nowWithRefreshOffset = Date.now() - fiveMinutuesInMilliseconds;
        return !this._jwtToken.token || new Date(this._jwtToken.utcExpiryDate) <= new Date(new Date(nowWithRefreshOffset).toUTCString());
    }
    
    private async getApiResponse<T>(url: string, method: string, data: any = null): Promise<T> {
        // Builds an object using the passed generic type from the the response's json body
        const response = await this.sendHttpRequest(url, method, data)

        // To avoid the app looking broken, replace json reserved characters before deserializing
        const responseText = (await response.text()).replaceAll('&amp;', '&');

        return JSON.parse(responseText) as T;
    }

    private async sendHttpRequest(url: string, method: string, data: any = null): Promise<Response> {
        // Ensure url has been passed
        if (!url) {
            throw 'Url must not be null or blank';
        }

        if (url.indexOf('/') === 0) {
            url = url.slice(1, url.length)
        }

        // Get URL depending on environment
        const apiUrl = this._baseUrl + url
        let apiResponse: Response = new Response();

        // To avoid potential timing issues invalidating jwt tokens, attempt to make the api call a few times if unsuccesful
        for (let retry = 0; retry < this._apiRetryMax + 1; retry++) {
            // Get new jwt token if required
            if (this.isJwtTokenRefreshRequired()) {
                /* To prevent our client recalling the server and getting multiple JWT tokens run ecclusively.
                   Once this process is unlocked if this is the first to call it will then call the API, if another process
                   unlocks before it will load the jwt so this will skip the auth API call */
                await this.mutex.runExclusive(async () => {
                    if (this.isJwtTokenRefreshRequired()) {
                        await this.getJwtToken();
                    }
                });
            }

            if (data) {
                data = JSON.stringify(data);
            }

            // Makes a request to the Acquaint Api , authenticates with the dbConectorToken and Acquaint JWT
            apiResponse = await fetch(apiUrl, {
                method,
                mode: 'cors',
                headers: {
                    'Authorization': 'Bearer ' + this._jwtToken.token,
                    'content-type': 'application/json'
                },
                body: data,
            });

            if (apiResponse.ok === true) {
                // Request successful, so return api response
                break;
            } else {
                // If the Api request failed then throw a new error with the body of the response (this will be our custom error message)
                await this.processFailedApiRequest(apiResponse, retry);
            }
        }

        return apiResponse;
    }

    private async processFailedApiRequest(apiResponse: Response, retryAttempt: number) {
        let responseText = '';

        // Only get response text for non-404 responses as the error text for 404 responses are hard coded
        if (apiResponse.status != 404) {
            responseText = await apiResponse.text();
        }

        switch (apiResponse.status) {
            case 401:
            case 403:
                // To avoid potential timing issues invalidating jwt tokens, reset the jwt token to cause the apiClient to re-get the jwt token in the next retry
                if (retryAttempt != this._apiRetryMax) {
                    this._jwtToken.token = '';
                    return;
                }

                throw new Error(`${responseText}`);                
            case 404:
                throw new Error(`The requested record could not be found, it may have been deleted.`)
            default:
                throw new Error(`${responseText}`);
        }
    }  
}
    
// Make api client available to Vue via dependency injection
export const HomemoverApiClientPlugin: Plugin = {
    install: (app: App) => {
        const client = new HomemoverApiClient();
        app.provide('HomemoverApiClient', client);
    }
}